import { useParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { CustomControlCode } from '@common/enums';
import type { IAdminProcess } from '@common/interfaces/adminProcesses';
import type { IPatient } from '@common/interfaces/patient';
import { ManagerAuthorization } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { ActivitiesSection } from '@frontend/components/pages/processes/newAdminProcessDetails/ActivitiesSection';
import { CancelModal } from '@frontend/components/pages/processes/newAdminProcessDetails/CancelModal';
import { ProcessInfoSection } from '@frontend/components/pages/processes/newAdminProcessDetails/ProcessInfoSection';
import { ProcessWorkflowModal } from '@frontend/components/pages/processes/newAdminProcessDetails/ProcessWorkflowModal';
import { TasksSection } from '@frontend/components/pages/processes/newAdminProcessDetails/TasksSection';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { getPatient } from '@frontend/services/api/patientsApiService';
import { cancelProcess } from '@frontend/services/api/processesApiService';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type {
    IServiceWorkflowStep,
    IServiceWorkflowStepActivity,
    IServiceWorkflowTransition,
} from '../services/workflowGraph/WorkflowGraph';
import { fetchJson } from '../../../store/fetch';
import { Loader } from '../../elements/common';
import { PatientAvatarBlock } from '../patients/PatientAvatarBlock';
import { PatientActiveInsuranceContextProvider } from '../patients/PatientInsuranceHistoryContext';
import { PatientSideBar } from '../patients/patientSideBar/PatientSideBar';
import { FailedSteps } from './newAdminProcessDetails/FailedSteps';
import { FinancialSection } from './newAdminProcessDetails/FinancialSection';
import { ServiceProcessActions } from './newAdminProcessDetails/ServiceProcessActions';
import { ThirdPartyMessages } from './newAdminProcessDetails/ThirdPartyMessages';
import { WorkflowSteps } from './newAdminProcessDetails/WorkflowSteps';

type ServiceActivity = {
    customControlCode: CustomControlCode;
    id: string;
    label: string;
    name: string;
    serviceId: string;
};

export const NewAdminProcessDetails = () => {
    const abortSignal = useAbortSignal();
    const { id } = useParams();
    const [isCancelProcessModalOpen, setIsCancelProcessModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState<IAdminProcess>();
    const [patient, setPatient] = useState<IPatient>();
    const [serviceWorkflowSteps, setServiceWorkflowSteps] = useState<IServiceWorkflowStep[]>([]);
    const [serviceWorkflowTransitions, setServiceWorkflowTransitions] = useState<IServiceWorkflowTransition[]>([]);
    const [serviceActivities, setServiceActivities] = useState<ServiceActivity[]>([]);
    const [serviceWorkflowStepActivities, setServiceWorkflowStepActivities] = useState<IServiceWorkflowStepActivity[]>(
        []
    );
    const [workflowModalIsOpen, setWorkflowModalIsOpen] = useState<boolean>(false);

    assertHasAuthorization(ManagerAuthorization.adminProcessRead);

    useEffect(() => {
        loadPatient();
        loadServiceWorkflow();
    }, [process]);

    useEffect(() => {
        if (id) {
            loadProcess(id);
        }
    }, [id]);

    if (!patient || !process) {
        return (
            <Loader
                centered={true}
                large={true}
            />
        );
    }

    return (
        <PatientActiveInsuranceContextProvider patientId={patient.id}>
            <PageTemplate
                sideBarNavigation={
                    <PatientSideBar
                        onReload={loadPatient}
                        patient={patient}
                    />
                }
                sideBarProfile={
                    <PatientAvatarBlock
                        patient={patient}
                        size="large"
                    />
                }
                topBarNavigation={
                    <PatientSideBar
                        onReload={loadPatient}
                        patient={patient}
                    />
                }
                topBarProfile={
                    <PatientAvatarBlock
                        patient={patient}
                        size="small"
                    />
                }
                width="medium"
            >
                <ProcessInfoSection
                    onShowCancelProcessModal={() => setIsCancelProcessModalOpen(true)}
                    onShowWorkflowModal={() => setWorkflowModalIsOpen(true)}
                    process={process}
                    reloadProcess={() => loadProcess(process.id)}
                />

                <ServiceProcessActions process={process} />

                <ActivitiesSection
                    process={process}
                    serviceActivities={serviceActivities}
                />
                {/* https://ksyos-it.atlassian.net/browse/CS-24519 */}
                <FinancialSection processNumber={process.processNumber} />

                <TasksSection process={process} />

                <WorkflowSteps
                    onReloadProcess={() => loadProcess(process.id)}
                    process={process}
                    serviceActivities={serviceActivities}
                    serviceWorkflowStepActivities={serviceWorkflowStepActivities}
                    serviceWorkflowSteps={serviceWorkflowSteps}
                    serviceWorkflowTransitions={serviceWorkflowTransitions}
                />

                <FailedSteps
                    processId={process.id}
                    processWorkflowSteps={process.processWorkflowSteps}
                    serviceWorkflowSteps={serviceWorkflowSteps}
                />

                <ThirdPartyMessages processId={process.id} />

                {workflowModalIsOpen && (
                    <ProcessWorkflowModal
                        isLoading={isLoading}
                        isOpen={workflowModalIsOpen}
                        onClose={() => setWorkflowModalIsOpen(false)}
                        onReloadProcess={() => loadProcess(process.id)}
                        process={process}
                        serviceActivities={serviceActivities}
                        serviceWorkflowStepActivities={serviceWorkflowStepActivities}
                        serviceWorkflowSteps={serviceWorkflowSteps}
                        serviceWorkflowTransitions={serviceWorkflowTransitions}
                    />
                )}

                {isCancelProcessModalOpen && (
                    <CancelModal
                        onClose={() => setIsCancelProcessModalOpen(false)}
                        onConfirm={() => handleCancelProcess(process.id)}
                        process={process}
                    />
                )}
            </PageTemplate>
        </PatientActiveInsuranceContextProvider>
    );

    async function loadProcess(processId: string) {
        setIsLoading(true);
        const url = new UrlBuilder(`/api/admin/processes/${processId}`).build();
        const result = await fetchJson(url, 'GET');
        setProcess(result.json);
        setIsLoading(false);
    }

    async function loadPatient() {
        if (!process) {
            return setPatient(undefined);
        }

        const result = await getPatient(
            process.patientId,
            {
                includeItemAuthorizations: ['canSeePatientDetails', 'canUpdate'],
            },
            abortSignal
        );

        setPatient(result);
    }

    async function loadServiceWorkflow() {
        if (!process) {
            return;
        }
        const url = new UrlBuilder(`/api/services/${process.service.id}/serviceWorkflow`).build();
        const result = await fetchJson(url, 'GET');
        setServiceWorkflowSteps(result.json.steps);
        setServiceWorkflowTransitions(result.json.transitions);
        setServiceActivities(result.json.activities);
        setServiceWorkflowStepActivities(result.json.stepActivities);
    }

    async function handleCancelProcess(processId: string): Promise<void> {
        await cancelProcess(processId, abortSignal);
        setIsCancelProcessModalOpen(false);
        await loadProcess(processId);
    }
};
